import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { projectsApi } from '../../../api/projects';
import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { PlusCircle, LayoutList, CalendarDays } from 'lucide-react';
import { TimeEntry } from '../../../types';
import { Button } from '../../../components/ui/atoms/button';
import { Dialog, DialogContent } from '../../../components/ui/organisms/dialog';
import { TimeEntryCalendar, getWeekStart } from '../../../components/ui/organisms/calendar-view';
import { ViewMode } from './interface';
import { TabButton } from './components';
import { ListView, CalendarTab, EditEntryDialog, FinalizeDayDialog } from './partials';
import { isEntryProjectLocked } from './utils';

export function EntriesPage() {
  const navigate = useNavigate();


  const [view, setViewState] = useState<ViewMode>(
    () => (localStorage.getItem('entriesView') === 'calendar' ? 'calendar' : 'list'),
  );
  const setView = (v: ViewMode) => {
    setViewState(v);
    localStorage.setItem('entriesView', v);
  };

  // ── Calendar week state ──
  const [calendarWeekStart, setCalendarWeekStart] = useState<Date>(() => getWeekStart(new Date()));

  // ── Edit state (shared between list and calendar) ──
  const [editEntry, setEditEntry] = useState<TimeEntry | null>(null);

  // A delete that would leave the day short of 8h doesn't go straight through:
  // the day is finalized instead, with the entry staged for removal so it is
  // deleted in the same transaction as whatever fills the gap.
  const [finalizeDay, setFinalizeDay] = useState<{ date: string; removeId: string } | null>(null);

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list().then((r) => r.data),
  });
  const { user: me } = useCurrentUser();

  // ─── Open edit (handles both list TimeEntry and calendar TimeEntryCalendar) ──

  const openEdit = (entry: TimeEntry | TimeEntryCalendar) => {
    if (isEntryProjectLocked(entry, projectsData)) return;
    const projectId  = 'projectId'  in entry ? entry.projectId  : entry.project.id;
    const phaseId    = 'phaseId'    in entry ? entry.phaseId    : entry.phase?.id ?? '';
    const jobTitleId = 'jobTitleId' in entry ? entry.jobTitleId : entry.jobTitle?.id ?? '';
    setEditEntry({ ...entry, projectId, phaseId, jobTitleId } as TimeEntry);
  };

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">My Time Entries</h1>
        <Button onClick={() => navigate('/employee/entries/new')}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Log Hours
        </Button>
      </div>

      {/* ── View tab switcher ── */}
      <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1 w-fit">
        <TabButton active={view === 'list'} onClick={() => setView('list')} icon={<LayoutList size={15} />} label="List View" />
        <TabButton active={view === 'calendar'} onClick={() => setView('calendar')} icon={<CalendarDays size={15} />} label="Calendar View" />
      </div>

      {view === 'list' ? (
        <ListView onEdit={openEdit} onFinalizeDay={(date, removeId) => setFinalizeDay({ date, removeId })} />
      ) : (
        <CalendarTab weekStart={calendarWeekStart} onWeekChange={setCalendarWeekStart} onEditEntry={openEdit} />
      )}

      {/* ── Edit Dialog (shared by both views) ── */}
      <Dialog
        open={!!editEntry}
        onOpenChange={(open) => {
          if (!open) setEditEntry(null);
        }}
      >
        {editEntry && (
          <EditEntryDialog entry={editEntry} onClose={() => setEditEntry(null)} />
        )}
      </Dialog>

      {/* ── Finalize day (opened by a delete that would leave the day short) ── */}
      <Dialog
        open={!!finalizeDay}
        onOpenChange={(open) => {
          if (!open) setFinalizeDay(null);
        }}
      >
        {finalizeDay && (
          <DialogContent
            // Backing out here abandons the deletion too, so a stray click
            // mustn't be read as a decision either way.
            onEscapeKeyDown={(e: any) => e.preventDefault()}
            onPointerDownOutside={(e: any) => e.preventDefault()}
            onInteractOutside={(e: any) => e.preventDefault()}
          >
            <FinalizeDayDialog
              date={finalizeDay.date}
              initialRemoved={[finalizeDay.removeId]}
              projects={projectsData ?? []}
              defaultJobTitleId={me?.jobTitleId ?? ''}
              onBack={() => setFinalizeDay(null)}
              onSaved={() => setFinalizeDay(null)}
            />
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
